import React from 'react'

import {
  ComputeEngineComparisonReportDataValues,
  ComputeEngineComparisonReportDataValues2
} from '@clinic/client'
import { formatDateTime } from '~/utils/date-time'

import { ColumnType } from '../components/Table'
import { DescriptionColumn, DiffColumn, T1T2LinkColumn } from './customColumns'

interface TableType<T = any> {
  key: string
  hideExpandData?: boolean
  columns?: ColumnType<T>[]
  subTables?: TableType<T>[]
}

const timeRangeTable: TableType<ComputeEngineComparisonReportDataValues2> = {
  key: 'basicInfo',
  subTables: [
    {
      key: 'comparisonTimeRange',
      columns: [
        {
          title: 'T1.START_TIME',
          dataIndex: 't1StartTime',
          render: (val, rec) => <span>{formatDateTime(val)}</span>
        },
        {
          title: 'T1.END_TIME',
          dataIndex: 't1EndTime',
          render: (val, rec) => <span>{formatDateTime(val)}</span>
        },
        {
          title: 'T2.START_TIME',
          dataIndex: 't2StartTime',
          render: (val, rec) => <span>{formatDateTime(val)}</span>
        },
        {
          title: 'T2.END_TIME',
          dataIndex: 't2EndTime',
          render: (val, rec) => <span>{formatDateTime(val)}</span>
        }
      ]
    }
  ]
}

// TODO: fullfill the type
const keyMetricsTable: TableType<any> = {
  key: 'keyMetricsComparison',
  hideExpandData: true,
  columns: [
    {
      title: 'Metrics',
      dataIndex: 'metrics'
    },
    { title: 'T1 value', dataIndex: 't1AverageValue' },
    { title: 'T2 value', dataIndex: 't2AverageValue' },
    {
      title: 'Diff',
      dataIndex: 'diff',
      render: (val, rec) => <DiffColumn value={val} />
    },
    {
      title: 'View',
      dataIndex: 'view',
      width: 60,
      render: (val, rec) => <T1T2LinkColumn dataIndex={rec.metrics} />
    }
  ]
}

const businessMetricsTable: TableType<ComputeEngineComparisonReportDataValues> =
  {
    key: 'businessMetricsComparison',
    columns: [
      { title: 'Metrics', dataIndex: 'metrics' },
      { title: 'T1 value-avg', dataIndex: 't1AverageValue' },
      { title: 'T2 value-avg', dataIndex: 't2AverageValue' },
      {
        title: 'Diff',
        dataIndex: 'diff',
        render: (val, rec) => <DiffColumn value={val} />
      },
      {
        title: 'Description',
        width: 500,
        dataIndex: 'description',
        render: (val, rec, idx) => (
          <DescriptionColumn
            k={rec.description?.key!}
            vars={rec.description?.variables!}
            // TODO: need to update swagger docs
            diff={rec.diff as unknown as number}
          />
        )
      },
      {
        title: 'View',
        dataIndex: 'view',
        width: 60,
        render: (val, rec) => <T1T2LinkColumn dataIndex={rec.metrics!} />
      }
    ]
  }

// TODO: type
const performanceTable: TableType<any> = {
  key: 'performanceMetricsComparison',
  subTables: [
    {
      key: 'performanceMetricsComparison.Perf_basic',
      columns: [
        {
          title: 'Metrics',
          dataIndex: 'metrics'
        },
        { title: 'T1 value', dataIndex: 't1AverageValue' },
        { title: 'T2 value', dataIndex: 't2AverageValue' },
        {
          title: 'Diff',
          dataIndex: 'diff',
          render: (val, rec) => <DiffColumn value={val} />
        },
        {
          title: 'Description',
          width: 500,
          dataIndex: 'description',
          render: (val, rec, idx) => (
            <DescriptionColumn
              k={rec.description?.key!}
              vars={rec.description?.variables!}
              diff={rec.diff!}
            />
          )
        },
        {
          title: 'View',
          dataIndex: 'view',
          width: 60,
          render: (val, rec) => <T1T2LinkColumn dataIndex={rec.metrics} />
        }
      ]
    },
    {
      key: 'performanceMetricsComparison.Perf_phase',
      columns: [
        {
          title: 'Metrics',
          dataIndex: 'metrics'
        },
        { title: 'T1 value', dataIndex: 't1AverageValue' },
        { title: 'T2 value', dataIndex: 't2AverageValue' },
        {
          title: 'Diff',
          dataIndex: 'diff',
          render: (val, rec) => <DiffColumn value={val} />
        },
        {
          title: 'Description',
          width: 500,
          dataIndex: 'description',
          render: (val, rec, idx) => (
            <DescriptionColumn
              k={rec.description?.key!}
              vars={rec.description?.variables!}
              diff={rec.diff!}
            />
          )
        },
        {
          title: 'View',
          dataIndex: 'view',
          width: 60,
          render: (val, rec) => <T1T2LinkColumn dataIndex={rec.metrics} />
        }
      ]
    },
    {
      key: 'performanceMetricsComparison.Perf_type',
      columns: [
        {
          title: 'Metrics',
          dataIndex: 'metrics'
        },
        { title: 'T1 value', dataIndex: 't1AverageValue' },
        { title: 'T2 value', dataIndex: 't2AverageValue' },
        {
          title: 'Diff',
          dataIndex: 'diff',
          render: (val, rec) => <DiffColumn value={val} />
        },
        {
          title: 'Description',
          width: 500,
          dataIndex: 'description',
          render: (val, rec, idx) => (
            <DescriptionColumn
              k={rec.description?.key!}
              vars={rec.description?.variables!}
              diff={rec.diff!}
            />
          )
        }
      ]
    },
    {
      key: 'performanceMetricsComparison.Perf_sqlexecute',
      columns: [
        {
          title: 'Metrics',
          dataIndex: 'metrics'
        },
        { title: 'T1 value', dataIndex: 't1AverageValue' },
        { title: 'T2 value', dataIndex: 't2AverageValue' },
        {
          title: 'Diff',
          dataIndex: 'diff',
          render: (val, rec) => <DiffColumn value={val} />
        },
        {
          title: 'Description',
          width: 500,
          dataIndex: 'description',
          render: (val, rec, idx) => (
            <DescriptionColumn
              k={rec.description?.key!}
              vars={rec.description?.variables!}
              diff={rec.diff!}
            />
          )
        }
      ]
    },
    {
      key: 'performanceMetricsComparison.Perf_durbytype',
      columns: [
        {
          title: 'Metrics',
          dataIndex: 'metrics'
        },
        { title: 'T1 value', dataIndex: 't1AverageValue' },
        { title: 'T2 value', dataIndex: 't2AverageValue' },
        {
          title: 'Diff',
          dataIndex: 'diff',
          render: (val, rec) => <DiffColumn value={val} />
        },
        {
          title: 'Description',
          width: 500,
          dataIndex: 'description',
          render: (val, rec, idx) => (
            <DescriptionColumn
              k={rec.description?.key!}
              vars={rec.description?.variables!}
              diff={rec.diff!}
            />
          )
        }
      ]
    },
    {
      key: 'performanceMetricsComparison.Perf_kvrequest',
      columns: [
        {
          title: 'Metrics',
          dataIndex: 'metrics'
        },
        { title: 'T1 value', dataIndex: 't1AverageValue' },
        { title: 'T2 value', dataIndex: 't2AverageValue' },
        {
          title: 'Diff',
          dataIndex: 'diff',
          render: (val, rec) => <DiffColumn value={val} />
        },
        {
          title: 'Description',
          width: 500,
          dataIndex: 'description',
          render: (val, rec, idx) => (
            <DescriptionColumn
              k={rec.description?.key!}
              vars={rec.description?.variables!}
              diff={rec.diff!}
            />
          )
        }
      ]
    },
    {
      key: 'performanceMetricsComparison.Perf_detailed',
      columns: [
        {
          title: 'Metrics',
          dataIndex: 'metrics'
        },
        { title: 'T1 value', dataIndex: 't1AverageValue' },
        { title: 'T2 value', dataIndex: 't2AverageValue' },
        {
          title: 'Diff',
          dataIndex: 'diff',
          render: (val, rec) => <DiffColumn value={val} />
        },
        {
          title: 'Description',
          width: 500,
          dataIndex: 'description',
          render: (val, rec, idx) => (
            <DescriptionColumn
              k={rec.description?.key!}
              vars={rec.description?.variables!}
              diff={rec.diff!}
            />
          )
        }
      ]
    }
  ]
}

const resourceTable: TableType<ComputeEngineComparisonReportDataValues> = {
  key: 'resourceMetricsComparison',
  columns: [
    {
      title: 'Metrics',
      dataIndex: 'metrics'
    },
    { title: 'T1 value', dataIndex: 't1AverageValue' },
    { title: 'T2 value', dataIndex: 't2AverageValue' },
    {
      title: 'Diff',
      dataIndex: 'diff',
      render: (val, rec) => <DiffColumn value={val} />
    },
    {
      title: 'Description',
      width: 500,
      dataIndex: 'description',
      render: (val, rec, idx) => (
        <DescriptionColumn
          k={rec.description?.key!}
          vars={rec.description?.variables!}
          // TODO: need to update swagger docs
          diff={rec.diff as unknown as number}
        />
      )
    },
    {
      title: 'View',
      dataIndex: 'view',
      width: 60,
      render: (val, rec) => <T1T2LinkColumn dataIndex={rec.metrics!} />
    }
  ]
}

const configTable: TableType<ComputeEngineComparisonReportDataValues> = {
  key: 'configurationComparison',
  columns: [
    { title: 'Components', dataIndex: 'components' },
    { title: 'Configuration item', dataIndex: 'configurationItems' },
    { title: 'T1 value', dataIndex: 't1Value' },
    { title: 'T2 value', dataIndex: 't2Value' }
  ]
}

export const tables: TableType<any>[] = [
  timeRangeTable,
  keyMetricsTable,
  businessMetricsTable,
  performanceTable,
  resourceTable,
  configTable
]
